import { DateTime } from "luxon";
import type {
  MRT_Cell,
  MRT_Row,
  MRT_RowData,
  MRT_TableInstance
} from "mantine-react-table-open";
import { useState } from "react";
import {
  DateInput,
  type DateInputProps
} from "../../../dates/DateInput/DateInput";

interface ControlledMantineEditDateInputProps<
  T extends MRT_RowData
> extends Pick<DateInputProps, "placeholder" | "onKeyDown"> {
  cell: MRT_Cell<T, Date | null>;
  columnId: string;
  errorText: string | undefined;
  label?: string;
  row: MRT_Row<T>;
  table: MRT_TableInstance<T>;
}

/** Custom DatePicker Edit component for mantine-react-table.
 * Inspired by this workaround: https://github.com/KevinVandy/mantine-react-table/discussions/8#discussioncomment-8759454
 */
function ControlledMantineEditDateInput<T extends MRT_RowData>({
  cell,
  columnId,
  errorText,
  label,
  placeholder,
  row,
  table,
  onKeyDown
}: ControlledMantineEditDateInputProps<T>) {
  const [value, setValue] = useState<Date | null>(cell.getValue());

  function saveInputValueToRowCache(newValue: Date | null) {
    const { editingRow, creatingRow } = table.getState();
    const isCreating = creatingRow?.id === row.id;
    const isEditing = editingRow?.id === row.id;

    //@ts-expect-error when using a generic type, the valuesCache is readonly
    row._valuesCache[columnId] = newValue;
    if (isCreating) {
      table.setCreatingRow(row);
    } else if (isEditing) {
      table.setEditingRow(row);
    }
  }

  function handleChange(newValue: DateTime | null) {
    const newDate = newValue?.toJSDate() ?? null;

    setValue(newDate);
    saveInputValueToRowCache(newDate);
  }

  const dateInputDate = value ? DateTime.fromJSDate(value) : null;

  return (
    <DateInput
      aria-label={label}
      error={errorText}
      placeholder={placeholder ?? "TT.MM.JJJJ"}
      value={dateInputDate}
      onChange={handleChange}
      onKeyDown={onKeyDown}
    />
  );
}

export { ControlledMantineEditDateInput };
